import { createNetworkError, createTimeoutError, createParseError } from './error';
import { debug } from './logger';
import { sessionCache } from './cache';

/**
 * 带超时的fetch操作
 * @param url 请求URL
 * @param options 请求选项
 * @param timeout 超时时间（毫秒）
 */
export const fetchWithTimeout = async (
  url: string,
  options: RequestInit = {},
  timeout: number = 5000
): Promise<Response> => {
  return new Promise((resolve, reject) => {
    // 设置超时
    const timeoutId = setTimeout(() => {
      reject(createTimeoutError(`请求超时：${timeout}ms`, { url }));
    }, timeout);

    fetch(url, options)
      .then(response => {
        clearTimeout(timeoutId);
        resolve(response);
      })
      .catch(error => {
        clearTimeout(timeoutId);
        reject(createNetworkError(`网络请求失败: ${error instanceof Error ? error.message : String(error)}`, { url, error }));
      });
  });
};

/**
 * 带缓存的JSON请求
 * @param url 请求URL
 * @param options 请求选项
 * @param cacheKey 缓存键
 * @param timeout 超时时间（毫秒）
 */
export const fetchJsonWithCache = async <T>(
  url: string,
  options: RequestInit = {},
  cacheKey: string,
  timeout: number = 5000
): Promise<T> => {
  // 检查缓存
  const cached = sessionCache.get(cacheKey);
  if (cached) {
    debug(`使用缓存数据: ${cacheKey}`);
    return cached as T;
  }

  try {
    const response = await fetchWithTimeout(url, options, timeout);

    if (!response.ok) {
      throw createNetworkError(`HTTP错误: ${response.status}`, { url, status: response.status });
    }

    const data = await response.json();

    // 缓存结果
    sessionCache.set(cacheKey, data);

    return data as T;
  } catch (error: unknown) {
    if (error instanceof SyntaxError) {
      throw createParseError('JSON解析失败', { url });
    }
    throw error;
  }
};

/**
 * 带重试的请求
 * @param fn 请求函数
 * @param retries 重试次数
 * @param delay 重试延迟（毫秒）
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error: unknown) {
    if (retries <= 0) {
      throw error;
    }

    debug(`请求失败，${retries}秒后重试...`, error);

    // 等待指定时间
    await new Promise(resolve => setTimeout(resolve, delay));

    // 递归重试
    return withRetry(fn, retries - 1, delay);
  }
};